import { useCallback, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import ModalHeader from "src/components/general/Modal/components/ModalHeader/ModalHeader";
import Modal from "src/components/general/Modal/Modal";
import { useSubmitFeedbackMutation } from "src/queries/feedback/feedback.mutation";
import { captureFeature } from "src/utils/analytics";
import { notify } from "src/utils/notification";
import FeedbackDialogAttachments from "./components/FeedbackDialogAttachments/FeedbackDialogAttachments";
import FeedbackDialogCategorySelect from "./components/FeedbackDialogCategorySelect/FeedbackDialogCategorySelect";
import {
  DEFAULT_CATEGORY,
  MAX_CHARS,
  SCREENSHOT_EXCLUDE_ATTR,
  SUBMIT_SUCCESS_MESSAGE,
} from "./FeedbackDialog.constants";
import type { Attachment, FeedbackCategoryId } from "./FeedbackDialog.types";

type FeedbackDialogProps = {
  open: boolean;
  onClose: () => void;
};

const FeedbackDialog = ({ open, onClose }: FeedbackDialogProps) => {
  const [category, setCategory] = useState<FeedbackCategoryId>(DEFAULT_CATEGORY);
  const [feedback, setFeedback] = useState("");
  const [attachment, setAttachment] = useState<Attachment | null>(null);

  const { mutateAsync: submitFeedback, isPending } = useSubmitFeedbackMutation();

  const canSubmit = feedback.trim().length > 0;

  const resetForm = useCallback(() => {
    setCategory(DEFAULT_CATEGORY);
    setFeedback("");
    setAttachment(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || isPending) {
      return;
    }

    try {
      await submitFeedback({
        category,
        feedback: feedback.trim(),
        page_url: window.location.href,
        attachment,
      });
    } catch {
      // dfetch already surfaced a network-error toast. Keep the dialog open with
      // the user's input intact so they can retry, and skip the success toast.
      return;
    }

    captureFeature("feedback_submit", {
      category,
      hasAttachment: attachment !== null,
    });
    notify(SUBMIT_SUCCESS_MESSAGE, { variant: "success" });
    resetForm();
    onClose();
  }, [
    attachment,
    canSubmit,
    category,
    feedback,
    isPending,
    onClose,
    resetForm,
    submitFeedback,
  ]);

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      size="fit"
      slotProps={{
        root: { ...{ [SCREENSHOT_EXCLUDE_ATTR]: "" } },
      }}
    >
      <div className="box-content grid w-130 overflow-hidden rounded bg-white shadow-xl dark:border dark:border-neutral-dark dark:bg-background-paper dark:shadow-2xl">
        <ModalHeader
          title="Help us improve by sharing your thoughts"
          onClose={handleClose}
        />

        <div className="grid gap-5 p-5">
          <FeedbackDialogCategorySelect value={category} onChange={setCategory} />

          <TextField
            label="Your feedback"
            multiline
            minRows={5}
            fullWidth
            value={feedback}
            onChange={(event) => setFeedback(event.target.value)}
            slotProps={{
              htmlInput: { maxLength: MAX_CHARS },
            }}
            helperText={
              <span className="block w-full text-right">
                {feedback.length}/{MAX_CHARS}
              </span>
            }
          />

          <FeedbackDialogAttachments
            attachment={attachment}
            onAttachmentChange={setAttachment}
          />

          <Button
            type="button"
            variant="contained"
            size="large"
            fullWidth
            loading={isPending}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Submit Feedback
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FeedbackDialog;
